"use client";

// Shared FAQ accordion used across marketing pages (home, lookups, pricing,
// governance, …). HeroUI accordion styled as spaced, bordered cards with a
// plus/minus circle indicator and brand-colored expanded question.

import type { ReactNode } from "react";
import { Accordion } from "@heroui/react";
import { CirclePlus, CircleMinus } from "@/components/icons";
import { cn } from "@/lib/utils";

export interface FaqItem {
  question: string;
  answer: ReactNode;
}

export function FaqAccordion({
  items,
  className,
}: {
  items: FaqItem[];
  className?: string;
}) {
  return (
    <div className={cn("heroui text-left", className)}>
      {/* Item ids are indices, so "0" opens the first question on every FAQ. */}
      <Accordion hideSeparator defaultExpandedKeys={["0"]} className="flex flex-col gap-3">
        {items.map((faq, i) => (
          <Accordion.Item
            key={i}
            id={String(i)}
            className="overflow-hidden rounded-[16px] bg-sidebar dark:bg-card transition-colors"
          >
            <Accordion.Heading>
              <Accordion.Trigger className="group text-sm font-medium text-text-primary aria-expanded:text-brand">
                {faq.question}
                {/* Must be ONE element: the Indicator falls back to its chevron
                    unless children is a single valid element. rotate-0! cancels
                    the default chevron spin. */}
                <Accordion.Indicator className="rotate-0!">
                  <span className="inline-flex shrink-0 items-center justify-center">
                    <CirclePlus className="size-4.5 text-text-quaternary group-aria-expanded:hidden" />
                    <CircleMinus className="hidden size-4.5 text-brand group-aria-expanded:block" />
                  </span>
                </Accordion.Indicator>
              </Accordion.Trigger>
            </Accordion.Heading>
            <Accordion.Panel>
              <Accordion.Body className="text-sm leading-relaxed text-text-tertiary">
                {faq.answer}
              </Accordion.Body>
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  );
}
